import { memo } from "react";

import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

import { DateInput } from "../../entrada-data";
import { YearPicker } from "../../seletor-ano";
import { DATE_PRESETS, getDatePresetRange, getFieldType } from "../helpers";
import { OptionsList } from "./OptionsList";
import type { DateRangeState } from "../types";

interface FilterFieldProps {
  group: string;
  field: any;
  searches: Record<string, string>;
  selected: Record<string, any[]>;
  dateRanges: Record<string, DateRangeState>;
  onToggle: (group: string, option: any, booleanMode?: boolean) => void;
  onBulkToggle: (group: string, optionsToToggle: any[], selectAll: boolean) => void;
  onSearchChange: (group: string, value: string) => void;
  onDateChange: (group: string, side: "from" | "to", value: string) => void;
  onYearChange: (group: string, year: string) => void;
}

/* Campo individual de filtro — varia por tipo (ano, data, booleano ou opções). */
export const FilterField = memo(function FilterField({
  group,
  field,
  searches,
  selected,
  dateRanges,
  onToggle,
  onBulkToggle,
  onSearchChange,
  onDateChange,
  onYearChange,
}: FilterFieldProps) {
  const options = field?.options;
  const label = field?.label ?? group;
  const type = getFieldType(options);

  if (type === "year") {
    return (
      <div className="pb-4 pt-2">
        <YearPicker
          value={selected[group]?.[0] ?? ""}
          onChange={(year: string) => onYearChange(group, year)}
        />
      </div>
    );
  }

  if (type === "date") {
    const dateRange = dateRanges[group];

    return (
      <div className="flex flex-col gap-4 pb-2 pt-2">
        <div className="flex flex-wrap gap-1.5">
          {DATE_PRESETS.map((preset) => (
            <button
              key={preset.key}
              type="button"
              onClick={() => {
                const range = getDatePresetRange(preset.key);
                onDateChange(group, "from", range.from);
                onDateChange(group, "to", range.to);
              }}
              className="rounded-full border border-transparent bg-secondary/50 px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {preset.label}
            </button>
          ))}
        </div>
        <div className="grid gap-2">
          <label className="text-xs font-medium text-muted-foreground">Data Inicial</label>
          <DateInput
            value={dateRange?.from ? new Date(dateRange.from) : null}
            onChange={(date: Date) => onDateChange(group, "from", date.toISOString())}
            placeholder="De"
          />
        </div>
        <div className="grid gap-2">
          <label className="text-xs font-medium text-muted-foreground">Data Final</label>
          <DateInput
            value={dateRange?.to ? new Date(dateRange.to) : null}
            onChange={(date: Date) => onDateChange(group, "to", date.toISOString())}
            placeholder="Até"
          />
        </div>
      </div>
    );
  }

  if (type === "boolean") {
    const isChecked = !!selected[group]?.[0];

    return (
      <div
        onClick={() => onToggle(group, !isChecked, true)}
        className={cn(
          "flex cursor-pointer items-center justify-between gap-3 rounded-lg border p-3 transition-all",
          isChecked
            ? "border-primary/20 bg-primary/10"
            : "border-transparent bg-transparent hover:bg-muted",
        )}
      >
        <span className="select-none text-sm font-medium">{label}</span>
        <Switch
          checked={isChecked}
          onCheckedChange={() => onToggle(group, !isChecked, true)}
          onClick={(e) => e.stopPropagation()}
          aria-label={label}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 pt-1">
      <Input
        placeholder={`Buscar ${label.toLowerCase()}...`}
        className="h-9 border-transparent bg-secondary/50 text-xs focus:border-primary"
        value={searches[group] || ""}
        onChange={(e) => onSearchChange(group, e.target.value)}
      />

      <OptionsList
        options={Array.isArray(options) ? options : []}
        group={group}
        searches={searches}
        selected={selected}
        onToggle={onToggle}
        onBulkToggle={onBulkToggle}
      />
    </div>
  );
});
